import { useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  Plus,
  Trash2,
  Briefcase,
  TrendingUp,
  Award,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp, SlideInRight } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { InvestmentRecord } from '@/lib/state/inverio-store';
import Badge, { calculateSeniorityLevel } from '@/components/Badge';
import { SkipButton } from '@/components/SkipButton';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAuth, supabase, isSupabaseConfigured } from '@/lib/supabase';
import { upsertTrackRecords } from '@/lib/supabase/track-record';
import { reportError } from '@/lib/errors';

type InvestmentStage = 'pre-seed' | 'seed' | 'series-a' | 'series-b' | 'series-c+';
type InvestmentType = 'direct' | 'syndicate';

interface NewInvestment {
  companyName: string;
  stage: InvestmentStage;
  investmentType: InvestmentType;
  year: string;
}

const STAGES: { value: InvestmentStage; label: string }[] = [
  { value: 'pre-seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B' },
  { value: 'series-c+', label: 'Series C+' },
];

const INVESTMENT_TYPES: { value: InvestmentType; label: string }[] = [
  { value: 'direct', label: 'Direct' },
  { value: 'syndicate', label: 'Syndicate' },
];

export default function InvestmentHistoryScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { user } = useAuth();
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const setInvestorProfile = useProfileStore((s) => s.setInvestorProfile);
  const skipOnboardingStep = useProfileStore((s) => s.skipOnboardingStep);

  const [investments, setInvestments] = useState<InvestmentRecord[]>(
    investorProfile?.investmentHistory ?? []
  );
  const [showForm, setShowForm] = useState(investments.length === 0);
  const [newInvestment, setNewInvestment] = useState<NewInvestment>({
    companyName: '',
    stage: 'seed',
    investmentType: 'direct',
    year: new Date().getFullYear().toString(),
  });

  const addInvestment = () => {
    if (!newInvestment.companyName.trim()) return;

    const investment: InvestmentRecord = {
      id: Date.now().toString(),
      companyName: newInvestment.companyName.trim(),
      stage: newInvestment.stage,
      investmentType: newInvestment.investmentType,
      date: `${newInvestment.year}-01-01`,
      source: 'manual',
    };

    setInvestments([...investments, investment]);
    setNewInvestment({
      companyName: '',
      stage: 'seed',
      investmentType: 'direct',
      year: new Date().getFullYear().toString(),
    });
    setShowForm(false);
  };

  const removeInvestment = (id: string) => {
    setInvestments(investments.filter((i) => i.id !== id));
  };

  const handleContinue = () => {
    // Track as skipped if no investments added
    if (investments.length === 0) {
      skipOnboardingStep('investment-history');
    }

    // Calculate years investing based on earliest investment
    let yearsInvesting = 0;
    if (investments.length > 0) {
      const years = investments.map((i) => parseInt(i.date.substring(0, 4), 10));
      const earliestYear = Math.min(...years);
      yearsInvesting = new Date().getFullYear() - earliestYear;
    }

    // Calculate seniority level
    const seniorityLevel = calculateSeniorityLevel(yearsInvesting, investments.length);

    // Update investor profile
    if (investorProfile) {
      setInvestorProfile({
        ...investorProfile,
        investmentHistory: investments,
        yearsInvesting,
        seniorityLevel,
      });
    }

    // Progressive DB save — fire-and-forget so navigation is instant.
    //
    // This used to write ONLY the aggregates below. The records themselves
    // stayed in the local store, so an investor could complete this step and
    // lose the lot by signing out or opening the web app — while the COUNT
    // persisted, letting the profile claim "12 investments" and list none.
    // `investor_track_record` is their server home now.
    //
    // Still non-blocking, but no longer lossy: the records remain in the local
    // store, and `useTrackRecordReconciliation` in the investor shell uploads
    // anything the server is missing on the next launch. Failure here delays
    // the write, it does not discard it.
    if (isSupabaseConfigured() && user) {
      // @ts-ignore - Supabase types mismatch
      supabase.from('investor_profiles').upsert({
        user_id: user.id,
        seniority_level: seniorityLevel,
        years_investing: yearsInvesting,
        portfolio_count: investments.length,
      }, { onConflict: 'user_id' }).then(({ error }) => {
        if (error) reportError(error, { context: 'InvestmentHistory.progressiveSave', silent: true });
      });

      void upsertTrackRecords(user.id, investments).catch((err) => {
        // NOT silent: losing an investor's track record is the bug this step
        // caused for months. Reconciliation will retry, but a recurring
        // failure needs to be visible.
        reportError(err, { context: 'InvestmentHistory.saveTrackRecord', level: 'warning' });
      });
    }

    router.push('/(onboarding)/portfolio-companies');
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => (currentYear - i).toString());

  // Preview seniority level
  const previewYears = investments.length > 0
    ? currentYear - Math.min(...investments.map((i) => parseInt(i.date.substring(0, 4), 10)))
    : 0;
  const previewSeniority = calculateSeniorityLevel(previewYears, investments.length);

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={FadeInDown.delay(100).duration(500)}
          className="px-5 pt-4 pb-4"
        >
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <View className="flex-row gap-2">
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.border.light }}
              />
            </View>
            <View className="w-10" />
          </View>
        </Animated.View>

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          className="flex-1"
        >
          <ScrollView className="flex-1 px-6" showsVerticalScrollIndicator={false}>
            {/* Title */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(600)}
              className="mb-6"
            >
              <View className="flex-row items-center gap-3 mb-2">
                <View
                  className="w-12 h-12 rounded-full items-center justify-center"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  <Briefcase size={24} color={colors.primary[600]} />
                </View>
                <View className="flex-1">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-xl font-bold"
                  >
                    Investment History
                  </Text>
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm"
                  >
                    Add your past investments to build credibility
                  </Text>
                </View>
              </View>
            </Animated.View>

            {/* Seniority Badge Preview */}
            {investments.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="mb-6"
              >
                <View
                  className="p-4 rounded-xl"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                  }}
                >
                  <View className="flex-row items-center gap-3">
                    <Award size={20} color={colors.primary[600]} />
                    <View className="flex-1">
                      <Text
                        style={{ color: colors.text.secondary }}
                        className="text-xs mb-1"
                      >
                        Your badge based on {investments.length} investment{investments.length !== 1 ? 's' : ''}
                      </Text>
                      <Badge type="investor-seniority" size="md" seniorityLevel={previewSeniority} />
                    </View>
                  </View>
                </View>
              </Animated.View>
            )}

            {/* Investment List */}
            {investments.length > 0 && (
              <Animated.View
                entering={FadeInUp.delay(350).duration(500)}
                className="mb-4"
              >
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-sm font-medium mb-3"
                >
                  Your Investments ({investments.length})
                </Text>
                <View className="gap-3">
                  {investments.map((investment, index) => (
                    <Animated.View
                      key={investment.id}
                      entering={SlideInRight.delay(index * 100).duration(400)}
                    >
                      <View
                        className="p-4 rounded-xl flex-row items-center"
                        style={{
                          backgroundColor: colors.surface.primary,
                          borderWidth: 1,
                          borderColor: colors.border.light,
                        }}
                      >
                        <View
                          className="w-10 h-10 rounded-full items-center justify-center mr-3"
                          style={{ backgroundColor: colors.primary[50] }}
                        >
                          <TrendingUp size={18} color={colors.primary[600]} />
                        </View>
                        <View className="flex-1">
                          <Text
                            style={{ color: colors.text.primary }}
                            className="font-semibold text-sm"
                          >
                            {investment.companyName}
                          </Text>
                          <Text
                            style={{ color: colors.text.tertiary }}
                            className="text-xs"
                          >
                            {STAGES.find((s) => s.value === investment.stage)?.label} •{' '}
                            {investment.investmentType === 'direct' ? 'Direct' : 'Syndicate'} •{' '}
                            {investment.date.substring(0, 4)}
                          </Text>
                        </View>
                        <Pressable
                          onPress={() => removeInvestment(investment.id)}
                          className="p-2"
                        >
                          <Trash2 size={18} color={colors.traffic.red} />
                        </Pressable>
                      </View>
                    </Animated.View>
                  ))}
                </View>
              </Animated.View>
            )}

            {/* Add Investment Form */}
            {showForm ? (
              <Animated.View
                entering={FadeInUp.duration(400)}
                className="mb-6"
              >
                <View
                  className="p-5 rounded-xl"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.primary[300],
                  }}
                >
                  <Text
                    style={{ color: colors.text.primary }}
                    className="font-semibold mb-4"
                  >
                    Add Investment
                  </Text>

                  {/* Company Name */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-medium mb-2"
                    >
                      Company Name
                    </Text>
                    <TextInput
                      value={newInvestment.companyName}
                      onChangeText={(text) =>
                        setNewInvestment({ ...newInvestment, companyName: text })
                      }
                      placeholder="e.g., Acme Inc."
                      placeholderTextColor={colors.text.tertiary}
                      className="px-4 py-3 rounded-xl text-base"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        color: colors.text.primary,
                      }}
                    />
                  </View>

                  {/* Stage */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-medium mb-2"
                    >
                      Stage
                    </Text>
                    <View className="flex-row flex-wrap gap-2">
                      {STAGES.map((stage) => (
                        <AnimatedPressable
                          key={stage.value}
                          onPress={() =>
                            setNewInvestment({ ...newInvestment, stage: stage.value })
                          }
                          className="px-4 py-2 rounded-full"
                          style={{
                            backgroundColor:
                              newInvestment.stage === stage.value
                                ? colors.primary[600]
                                : colors.surface.secondary,
                          }}
                        >
                          <Text
                            style={{
                              color:
                                newInvestment.stage === stage.value
                                  ? 'white'
                                  : colors.text.secondary,
                            }}
                            className="text-sm font-medium"
                          >
                            {stage.label}
                          </Text>
                        </AnimatedPressable>
                      ))}
                    </View>
                  </View>

                  {/* Investment Type */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-medium mb-2"
                    >
                      Investment Type
                    </Text>
                    <View className="flex-row gap-2">
                      {INVESTMENT_TYPES.map((type) => (
                        <AnimatedPressable
                          key={type.value}
                          onPress={() =>
                            setNewInvestment({ ...newInvestment, investmentType: type.value })
                          }
                          className="flex-1 py-3 rounded-xl items-center"
                          style={{
                            backgroundColor:
                              newInvestment.investmentType === type.value
                                ? colors.primary[600]
                                : colors.surface.secondary,
                          }}
                        >
                          <Text
                            style={{
                              color:
                                newInvestment.investmentType === type.value
                                  ? 'white'
                                  : colors.text.secondary,
                            }}
                            className="font-medium"
                          >
                            {type.label}
                          </Text>
                        </AnimatedPressable>
                      ))}
                    </View>
                  </View>

                  {/* Year */}
                  <View className="mb-4">
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-medium mb-2"
                    >
                      Year
                    </Text>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      style={{ flexGrow: 0 }}
                    >
                      <View className="flex-row gap-2">
                        {years.map((year) => (
                          <AnimatedPressable
                            key={year}
                            onPress={() => setNewInvestment({ ...newInvestment, year })}
                            className="px-4 py-2 rounded-full"
                            style={{
                              backgroundColor:
                                newInvestment.year === year
                                  ? colors.primary[600]
                                  : colors.surface.secondary,
                            }}
                          >
                            <Text
                              style={{
                                color:
                                  newInvestment.year === year
                                    ? 'white'
                                    : colors.text.secondary,
                              }}
                              className="text-sm font-medium"
                            >
                              {year}
                            </Text>
                          </AnimatedPressable>
                        ))}
                      </View>
                    </ScrollView>
                  </View>

                  {/* Form Actions */}
                  <View className="flex-row gap-3">
                    <AnimatedPressable
                      onPress={() => setShowForm(false)}
                      className="flex-1 py-3 rounded-xl items-center"
                      style={{
                        backgroundColor: colors.surface.secondary,
                      }}
                    >
                      <Text
                        style={{ color: colors.text.secondary }}
                        className="font-medium"
                      >
                        Cancel
                      </Text>
                    </AnimatedPressable>
                    <AnimatedPressable
                      onPress={addInvestment}
                      disabled={!newInvestment.companyName.trim()}
                      className="flex-1 py-3 rounded-xl items-center"
                      style={{
                        backgroundColor: newInvestment.companyName.trim()
                          ? colors.primary[600]
                          : colors.border.light,
                      }}
                    >
                      <Text
                        style={{
                          color: newInvestment.companyName.trim()
                            ? 'white'
                            : colors.text.tertiary,
                        }}
                        className="font-medium"
                      >
                        Add
                      </Text>
                    </AnimatedPressable>
                  </View>
                </View>
              </Animated.View>
            ) : (
              <Animated.View
                entering={FadeInUp.delay(400).duration(500)}
                className="mb-6"
              >
                <AnimatedPressable
                  onPress={() => setShowForm(true)}
                  className="flex-row items-center justify-center gap-2 py-4 rounded-xl"
                  style={{
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.light,
                    borderStyle: 'dashed',
                  }}
                >
                  <Plus size={20} color={colors.primary[600]} />
                  <Text
                    style={{ color: colors.primary[600] }}
                    className="font-medium"
                  >
                    Add Investment
                  </Text>
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* Info Box */}
            <Animated.View
              entering={FadeInUp.delay(500).duration(500)}
              className="mb-6"
            >
              <View
                className="p-4 rounded-xl"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-xs leading-5"
                >
                  💡 Adding more investments helps build your credibility. Startups
                  can see your experience level through your Investor Seniority badge.
                </Text>
              </View>
            </Animated.View>

            <View className="h-8" />
          </ScrollView>
        </KeyboardAvoidingView>

        {/* Continue Button */}
        <Animated.View
          entering={FadeInUp.delay(600).duration(500)}
          className="px-5 pb-8"
        >
          <AnimatedPressable
            onPress={handleContinue}
            className="rounded-xl py-4 flex-row items-center justify-center gap-2"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text
              style={{ color: colors.text.inverse }}
              className="font-semibold text-base"
            >
              {investments.length > 0 ? 'Continue' : 'Skip for now'}
            </Text>
          </AnimatedPressable>
        </Animated.View>
      </SafeAreaView>
    </View>
  );
}
